import { useRef, useState } from 'react';
import { motion } from 'framer-motion';

interface MagneticButtonProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
}

const MagneticButton = ({ children, className = '', onClick, variant = 'primary' }: MagneticButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!ref.current) return;
    
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const distanceX = e.clientX - centerX;
    const distanceY = e.clientY - centerY;
    
    setPosition({
      x: distanceX * 0.12,
      y: distanceY * 0.12,
    });
  };

  const handleMouseLeave = () => {
    setPosition({ x: 0, y: 0 });
  };

  const baseStyles = 'relative px-8 py-4 rounded-xl font-semibold text-sm transition-all duration-300 overflow-hidden';
  
  const variantStyles = {
    primary: 'bg-gradient-to-r from-[#0066FF] via-[#00A8E8] to-[#00D4AA] text-white shadow-lg shadow-[#00D4AA]/20 hover:shadow-[#00D4AA]/30',
    secondary: 'bg-white/80 text-slate-700 border border-slate-200 hover:bg-white hover:border-[#00D4AA]/30 hover:shadow-lg hover:shadow-[#00D4AA]/10',
    outline: 'bg-transparent text-slate-700 border-2 border-[#0066FF]/30 hover:border-[#00D4AA] hover:text-[#00D4AA] hover:bg-[#00D4AA]/5',
  };

  return (
    <motion.button
      ref={ref}
      className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      animate={{ x: position.x, y: position.y }}
      transition={{ type: 'spring', stiffness: 150, damping: 15, mass: 0.1 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Shimmer effect */}
      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
      
      {/* Glow effect for primary */}
      {variant === 'primary' && (
        <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#0066FF] to-[#00D4AA] blur-xl opacity-30 -z-10" />
      )}
      
      <span className="relative z-10 flex items-center justify-center gap-2">
        {children}
      </span>
    </motion.button>
  );
};

export default MagneticButton;
